import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit';
import reportsAPI from '@/api/reports';
import { normalizeData } from '@/utils/jsonapi';
import { getApiErrorMessage } from '@/utils/apiErrors'

/**
 * Entity adapter for normalized report state management
 * Provides CRUD operations and selectors for report entities
 */
const reportsAdapter = createEntityAdapter({
  selectId: (report) => report.id,
  sortComparer: (a, b) => (a.name || '').localeCompare(b.name || ''),
});

/**
 * Async thunk to fetch all reports
 * API already returns normalized data from JSON:API response
 */
export const fetchReportsThunk = createAsyncThunk(
  'reports/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await reportsAPI.getAll(params);
      // Normalize JSON:API response in slice for consistency
      return {
        data: normalizeData(jsonApiResponse.data || []),
        meta: jsonApiResponse.meta,
        included: jsonApiResponse.included
      };
    } catch (error) {
      // Extract error message from various possible formats
      let errorMessage = 'Failed to load reports';

      if (error.response?.data?.errors?.[0]?.detail) {
        // JSON:API error format
        errorMessage = error.response.data.errors[0].detail;
      } else if (error.apiError?.message) {
        errorMessage = error.apiError.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to fetch a single report by ID
 * API already returns normalized data from JSON:API response
 */
export const fetchReportByIdThunk = createAsyncThunk(
  'reports/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await reportsAPI.getById(id);
      // Normalize in slice for consistency
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to load report');
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Build the API payload for a report create/update from report-builder state.
 *
 * `metrics` holds display objects for the wizard; the serializer's write-only
 * field is `metric_configs`. `parameters` is dropped here on purpose — date
 * range and grouping live on the separate ReportParameter record and are saved
 * through /reports/{id}/parameters/ (see reportsAPI.updateParameters).
 *
 * @param {Object} reportData - Report builder state
 * @returns {Object} Payload with serializer field names
 */
function buildReportPayload(reportData) {
  const { metrics, parameters, ...rest } = reportData;

  return {
    ...rest,
    metric_configs:
      metrics?.map((m, idx) => ({
        metric_id: m.metric_id || m.id,
        sort_order: m.sort_order ?? idx,
        display_config: m.display_config || {},
      })) || [],
  };
}

/**
 * Async thunk to create a new report
 * Transforms metrics array to metric_configs with sort_order
 * API already returns normalized data from JSON:API response
 */
export const createReportThunk = createAsyncThunk(
  'reports/create',
  async (reportData, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await reportsAPI.create(buildReportPayload(reportData));
      // Normalize in slice for consistency
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to create report');
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to update an existing report
 * API already returns normalized data from JSON:API response
 */
export const updateReportThunk = createAsyncThunk(
  'reports/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await reportsAPI.update(id, buildReportPayload(data));
      // Normalize in slice for consistency
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to update report');
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to delete a report
 */
export const deleteReportThunk = createAsyncThunk(
  'reports/delete',
  async (id, { rejectWithValue }) => {
    try {
      await reportsAPI.delete(id);
      return id;
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to delete report');
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to duplicate a report
 * API already returns normalized data from JSON:API response
 */
export const duplicateReportThunk = createAsyncThunk(
  'reports/duplicate',
  async (id, { rejectWithValue }) => {
    try {
      const report = await reportsAPI.duplicate(id);
      // API returns normalized report object
      return report;
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to duplicate report');
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to run a report
 */
export const runReportThunk = createAsyncThunk(
  'reports/run',
  async ({ id, params = {} }, { rejectWithValue }) => {
    try {
      const response = await reportsAPI.run(id, params);
      return response;
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to run report');
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Reports slice with state management for report CRUD operations
 */
const reportsSlice = createSlice({
  name: 'reports',
  initialState: reportsAdapter.getInitialState({
    currentReport: null,
    loading: false,
    error: null,
    runResult: null,
    runLoading: false,
    pagination: null, // Store pagination metadata
    included: null, // Store included resources for relationships
  }),
  reducers: {
    setCurrentReport: (state, action) => {
      state.currentReport = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearRunResult: (state) => {
      state.runResult = null;
    },
    upsertMany: (state, action) => {
      reportsAdapter.upsertMany(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Reports
      .addCase(fetchReportsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportsThunk.fulfilled, (state, action) => {
        state.loading = false;
        reportsAdapter.setAll(state, action.payload?.data || []);
        // Store pagination metadata
        state.pagination = action.payload?.meta?.pagination || null;
        // Store included resources for relationship lookups
        state.included = action.payload?.included || null;
      })
      .addCase(fetchReportsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Report by ID
      .addCase(fetchReportByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        reportsAdapter.upsertOne(state, action.payload);
        state.currentReport = action.payload;
      })
      .addCase(fetchReportByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Report
      .addCase(createReportThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReportThunk.fulfilled, (state, action) => {
        state.loading = false;
        reportsAdapter.addOne(state, action.payload);
        state.currentReport = action.payload;
      })
      .addCase(createReportThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Report
      .addCase(updateReportThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReportThunk.fulfilled, (state, action) => {
        state.loading = false;
        reportsAdapter.upsertOne(state, action.payload);
        if (state.currentReport?.id === action.payload.id) {
          state.currentReport = action.payload;
        }
      })
      .addCase(updateReportThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Report
      .addCase(deleteReportThunk.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        // Optimistically remove from list
        reportsAdapter.removeOne(state, action.meta.arg);
      })
      .addCase(deleteReportThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteReportThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Note: Items already removed optimistically, may need to re-fetch on error
      })
      // Duplicate Report
      .addCase(duplicateReportThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(duplicateReportThunk.fulfilled, (state, action) => {
        state.loading = false;
        reportsAdapter.addOne(state, action.payload);
      })
      .addCase(duplicateReportThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Run Report
      .addCase(runReportThunk.pending, (state) => {
        state.runLoading = true;
        state.error = null;
      })
      .addCase(runReportThunk.fulfilled, (state, action) => {
        state.runLoading = false;
        state.runResult = action.payload;
      })
      .addCase(runReportThunk.rejected, (state, action) => {
        state.runLoading = false;
        state.error = action.payload;
      });
  },
});

export const { setCurrentReport, clearError, clearRunResult, upsertMany } = reportsSlice.actions;

// Adapter selectors
export const {
  selectAll: selectAllReports,
  selectById: selectReportById,
} = reportsAdapter.getSelectors((state) => state.reports || reportsAdapter.getInitialState());

// Additional selectors
export const selectReports = selectAllReports; // Alias for backward compatibility
export const selectCurrentReport = (state) => state.reports?.currentReport || null;
export const selectReportsLoading = (state) => state.reports?.loading || false;
export const selectReportsError = (state) => state.reports?.error || null;
export const selectRunResult = (state) => state.reports?.runResult || null;
export const selectRunLoading = (state) => state.reports?.runLoading || false;
export const selectReportsPagination = (state) => state.reports?.pagination || null;
export const selectReportsIncluded = (state) => state.reports?.included || null;

export default reportsSlice.reducer;
